import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Plus, Play, Trash2, Edit, BookOpen, Zap, MoreVertical, ChevronRight, GripVertical, Clock, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import type { Playbook, PlaybookStep, AiAgent } from "@shared/schema";

const ACTION_TYPES = [
  { value: "ai_prompt", label: "AI Prompt", description: "Send a prompt to an AI agent" },
  { value: "create_source", label: "Create Source", description: "Create a new reference source" },
  { value: "create_thread", label: "Create Thread", description: "Start a new chat thread" },
  { value: "add_reminder", label: "Add Reminder", description: "Schedule a reminder" },
  { value: "wait_approval", label: "Wait for Approval", description: "Pause until user approves" },
  { value: "conditional", label: "Conditional", description: "Branch based on conditions" },
];

const CATEGORIES = [
  { value: "research", label: "Research", icon: "🔍" },
  { value: "writing", label: "Writing", icon: "✍️" },
  { value: "review", label: "Review", icon: "📋" },
  { value: "organization", label: "Organization", icon: "📁" },
  { value: "general", label: "General", icon: "⚡" },
];

export default function PlaybookBuilder() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedPlaybook, setSelectedPlaybook] = useState<string | null>(null);
  const [isAddStepOpen, setIsAddStepOpen] = useState(false);
  
  const [newPlaybook, setNewPlaybook] = useState({
    name: "",
    description: "",
    icon: "📋",
    category: "general",
    estimatedDuration: "",
  });

  const [newStep, setNewStep] = useState({
    name: "",
    description: "",
    actionType: "ai_prompt",
    requiresApproval: false,
    timeoutMinutes: "60",
  });

  const { data: playbooks = [], isLoading } = useQuery<Playbook[]>({
    queryKey: ["/api/playbooks"],
  });

  const { data: agents = [] } = useQuery<AiAgent[]>({
    queryKey: ["/api/ai-agents"],
  });

  const { data: selectedPlaybookData } = useQuery<Playbook & { steps: PlaybookStep[] }>({
    queryKey: ["/api/playbooks", selectedPlaybook],
    enabled: !!selectedPlaybook,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof newPlaybook) => {
      return apiRequest("POST", "/api/playbooks", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/playbooks"] });
      setIsCreateOpen(false);
      setNewPlaybook({ name: "", description: "", icon: "📋", category: "general", estimatedDuration: "" });
      toast({ title: "Playbook created successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to create playbook", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/playbooks/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/playbooks"] });
      if (selectedPlaybook) setSelectedPlaybook(null);
      toast({ title: "Playbook deleted" });
    },
  });

  const runMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("POST", `/api/playbooks/${id}/run`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/playbook-runs"] });
      toast({ title: "Playbook started", description: "Your playbook is now running" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to start playbook", description: error.message, variant: "destructive" });
    },
  });

  const addStepMutation = useMutation({
    mutationFn: async (data: typeof newStep) => {
      return apiRequest("POST", `/api/playbooks/${selectedPlaybook}/steps`, {
        ...data,
        requiresApproval: data.requiresApproval ? "true" : "false",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/playbooks", selectedPlaybook] });
      setIsAddStepOpen(false);
      setNewStep({ name: "", description: "", actionType: "ai_prompt", requiresApproval: false, timeoutMinutes: "60" });
      toast({ title: "Step added" });
    },
  });

  const deleteStepMutation = useMutation({
    mutationFn: async (stepId: string) => {
      return apiRequest("DELETE", `/api/playbook-steps/${stepId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/playbooks", selectedPlaybook] });
      toast({ title: "Step deleted" });
    },
  });

  const getCategoryInfo = (category: string) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[4];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-page-title">Playbook Builder</h1>
          <p className="text-muted-foreground mt-1">Create and manage multi-step AI workflows</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-create-playbook">
              <Plus className="h-4 w-4 mr-2" />
              New Playbook
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Playbook</DialogTitle>
              <DialogDescription>Design a reusable workflow for automating tasks</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  placeholder="e.g., Research & Summarize"
                  value={newPlaybook.name}
                  onChange={(e) => setNewPlaybook(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="input-playbook-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="What does this playbook do?"
                  value={newPlaybook.description}
                  onChange={(e) => setNewPlaybook(prev => ({ ...prev, description: e.target.value }))}
                  data-testid="input-playbook-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={newPlaybook.category}
                    onValueChange={(value) => setNewPlaybook(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger data-testid="select-playbook-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.icon} {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Estimated Duration</Label>
                  <Input
                    placeholder="e.g., 15 minutes"
                    value={newPlaybook.estimatedDuration}
                    onChange={(e) => setNewPlaybook(prev => ({ ...prev, estimatedDuration: e.target.value }))}
                    data-testid="input-playbook-duration"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
              <Button 
                onClick={() => createMutation.mutate(newPlaybook)}
                disabled={!newPlaybook.name || createMutation.isPending}
                data-testid="button-save-playbook"
              >
                {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Playbook
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Your Playbooks</h2>
          {playbooks.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No Playbooks Yet</h3>
                <p className="text-sm text-muted-foreground mb-4">Create your first playbook to automate workflows</p>
                <Button onClick={() => setIsCreateOpen(true)} data-testid="button-create-first-playbook">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Playbook
                </Button>
              </CardContent>
            </Card>
          ) : (
            playbooks.map(playbook => {
              const catInfo = getCategoryInfo(playbook.category || "general");
              return (
                <Card 
                  key={playbook.id} 
                  className={`cursor-pointer transition-all hover-elevate ${selectedPlaybook === playbook.id ? "ring-2 ring-primary" : ""}`}
                  onClick={() => setSelectedPlaybook(playbook.id)}
                  data-testid={`card-playbook-${playbook.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{playbook.icon || catInfo.icon}</span>
                        <div>
                          <CardTitle className="text-base">{playbook.name}</CardTitle>
                          <Badge variant="secondary" className="mt-1">{catInfo.label}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            runMutation.mutate(playbook.id);
                          }}
                          disabled={runMutation.isPending}
                          data-testid={`button-run-playbook-${playbook.id}`}
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMutation.mutate(playbook.id);
                          }}
                          data-testid={`button-delete-playbook-${playbook.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2">{playbook.description || "No description"}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      {playbook.estimatedDuration && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {playbook.estimatedDuration}
                        </span>
                      )}
                      <span>Used {playbook.usageCount || 0} times</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        <div className="lg:col-span-2">
          {selectedPlaybook && selectedPlaybookData ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <span className="text-2xl">{selectedPlaybookData.icon || "📋"}</span>
                      {selectedPlaybookData.name}
                    </CardTitle>
                    <CardDescription>{selectedPlaybookData.description}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => runMutation.mutate(selectedPlaybook)}
                      disabled={runMutation.isPending}
                      data-testid="button-run-selected-playbook"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Run Playbook
                    </Button>
                    <Dialog open={isAddStepOpen} onOpenChange={setIsAddStepOpen}>
                      <DialogTrigger asChild>
                        <Button data-testid="button-add-step">
                          <Plus className="h-4 w-4 mr-2" />
                          Add Step
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Step</DialogTitle>
                          <DialogDescription>Add a new step to your playbook</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label>Step Name</Label>
                            <Input
                              placeholder="e.g., Analyze content"
                              value={newStep.name}
                              onChange={(e) => setNewStep(prev => ({ ...prev, name: e.target.value }))}
                              data-testid="input-step-name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                              placeholder="What does this step do?"
                              value={newStep.description}
                              onChange={(e) => setNewStep(prev => ({ ...prev, description: e.target.value }))}
                              data-testid="input-step-description"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Action Type</Label>
                            <Select
                              value={newStep.actionType}
                              onValueChange={(value) => setNewStep(prev => ({ ...prev, actionType: value }))}
                            >
                              <SelectTrigger data-testid="select-step-action">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ACTION_TYPES.map(action => (
                                  <SelectItem key={action.value} value={action.value}>
                                    <div>
                                      <div className="font-medium">{action.label}</div>
                                      <div className="text-xs text-muted-foreground">{action.description}</div>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <Label>Requires Approval</Label>
                              <p className="text-xs text-muted-foreground">Pause for user confirmation</p>
                            </div>
                            <Switch
                              checked={newStep.requiresApproval}
                              onCheckedChange={(checked) => setNewStep(prev => ({ ...prev, requiresApproval: checked }))}
                              data-testid="switch-step-approval"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsAddStepOpen(false)}>Cancel</Button>
                          <Button 
                            onClick={() => addStepMutation.mutate(newStep)}
                            disabled={!newStep.name || addStepMutation.isPending}
                            data-testid="button-save-step"
                          >
                            Add Step
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <h3 className="font-medium mb-4">Workflow Steps</h3>
                {(!selectedPlaybookData.steps || selectedPlaybookData.steps.length === 0) ? (
                  <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                    <Zap className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No steps yet. Add steps to build your workflow.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedPlaybookData.steps.map((step, index) => {
                      const actionInfo = ACTION_TYPES.find(a => a.value === step.actionType);
                      return (
                        <div 
                          key={step.id}
                          className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30"
                          data-testid={`step-${step.id}`}
                        >
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{step.name}</span>
                              <Badge variant="outline" className="text-xs">{actionInfo?.label || step.actionType}</Badge>
                              {step.requiresApproval === "true" && (
                                <Badge variant="secondary" className="text-xs">Needs Approval</Badge>
                              )}
                            </div>
                            {step.description && (
                              <p className="text-sm text-muted-foreground truncate">{step.description}</p>
                            )}
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => deleteStepMutation.mutate(step.id)}
                            data-testid={`button-delete-step-${step.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          {index < selectedPlaybookData.steps.length - 1 && (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="h-full flex items-center justify-center min-h-[400px]">
              <CardContent className="text-center">
                <BookOpen className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Select a Playbook</h3>
                <p className="text-muted-foreground">Choose a playbook from the list to view and edit its steps</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
